import { NgTemplateOutlet } from '@angular/common'
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  contentChild,
  inject,
  input,
  signal,
  TemplateRef,
  viewChild,
} from '@angular/core'
import { ControlValueAccessor, FormsModule, NG_VALUE_ACCESSOR } from '@angular/forms'

import { TranslatePipe } from '@ngx-translate/core'
import { isEqual } from 'lodash-es'
import { Button } from 'primeng/button'
import { Checkbox } from 'primeng/checkbox'
import { Chip } from 'primeng/chip'
import { IconField } from 'primeng/iconfield'
import { InputIcon } from 'primeng/inputicon'
import { InputText } from 'primeng/inputtext'
import { MultiSelect } from 'primeng/multiselect'

import { DeviceService } from '@ti/core/common/services'
import { SearchIconComponent } from '@ti/core/icons/search'

import { BottomDialogComponent } from '../../layout/components/bottom-dialog/bottom-dialog.component'
import { multiSelectPt } from '../../prime-ng/overrides/multiselect'

export interface MultiselectOptionContext<V> {
  $implicit: V
}

@Component({
  selector: 'app-multiselect',
  templateUrl: 'multiselect.component.html',
  styleUrls: ['multiselect.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    BottomDialogComponent,
    Button,
    Checkbox,
    Chip,
    FormsModule,
    IconField,
    InputIcon,
    InputText,
    MultiSelect,
    NgTemplateOutlet,
    SearchIconComponent,
    TranslatePipe,
  ],
  providers: [{ provide: NG_VALUE_ACCESSOR, useExisting: MultiselectComponent, multi: true }],
})
export class MultiselectComponent<V = unknown> implements ControlValueAccessor {
  protected readonly deviceService = inject(DeviceService)
  protected readonly multiSelectPt = multiSelectPt
  /** Short lists size the sheet to their content instead of reserving room for search results. */
  protected readonly SHORT_LIST_SIZE = 10

  public readonly options = input<V[]>([])
  public readonly optionLabel = input<string | undefined>(undefined)
  public readonly optionValue = input<string | undefined>(undefined)
  public readonly dataKey = input<string | undefined>()
  public readonly placeholder = input<string>('')
  public readonly maxSelectedLabels = input<number>(10)
  public readonly filter = input<boolean>(true)
  public readonly filterPlaceHolder = input<string>('')
  public readonly mobilePanelTitle = input<string>('')
  public readonly showHeader = input<boolean>(true)
  public readonly showClear = input<boolean>(true)
  public readonly normalizeToArrayOnClear = input<boolean>(true)
  public readonly focusOnHover = input<boolean>(false)
  public readonly displaySelectedLabel = input<boolean>(true)
  public readonly display = input<'comma' | 'chip'>('comma')
  public readonly styleClass = input<string>('')

  /** Renders an option in the panel and in the mobile sheet. */
  protected readonly itemTemplate = contentChild<TemplateRef<MultiselectOptionContext<V>>>('item')

  protected readonly value = signal<unknown[]>([])
  /** The selection being edited in the mobile sheet until Apply. */
  protected readonly _value = signal<unknown[]>([])
  protected readonly isDisabled = signal<boolean>(false)

  protected readonly visible = signal<boolean>(false)
  protected readonly searchQuery = signal<string>('')
  protected readonly pMultiSelect = viewChild(MultiSelect)

  protected readonly selectedValues = computed(() => {
    const value = this.value()
    return Array.isArray(value) ? value : []
  })

  protected readonly visibleChips = computed(() =>
    this.selectedValues().slice(0, this.maxSelectedLabels()),
  )
  protected readonly hiddenChipsCount = computed(() =>
    Math.max(0, this.selectedValues().length - this.maxSelectedLabels()),
  )

  protected readonly filteredOptions = computed(() => {
    const search = this.searchQuery().trim().toLocaleLowerCase()
    const options = this.options() ?? []
    if (!search) return options
    return options.filter((option) =>
      this.getOptionLabel(option).toLocaleLowerCase().includes(search),
    )
  })

  /** Toggle all only touches the enabled options the search leaves visible. */
  protected readonly toggleableValues = computed(() =>
    this.filteredOptions()
      .filter((option) => !this.isOptionDisabled(option))
      .map((option) => this.getOptionValue(option)),
  )

  protected readonly isAllSelected = computed(() => {
    const values = this.toggleableValues()
    const selected = this._value()
    return values.length > 0 && values.every((value) => this.includesValue(selected, value))
  })

  protected onChanged?: (value: unknown[]) => void
  protected onTouched?: () => void

  public writeValue(value: unknown[] | null): void {
    this.value.set(value || [])
    this._value.set(value || [])
  }

  public registerOnChange(fn: (value: unknown[]) => void): void {
    this.onChanged = fn
  }

  public registerOnTouched(fn: () => void): void {
    this.onTouched = fn
  }

  public setDisabledState(isDisabled: boolean): void {
    this.isDisabled.set(isDisabled)
    // An open sheet would let Apply write into the disabled control
    if (isDisabled) this.cancelValue()
  }

  protected updateValue(value: unknown[] | null): void {
    // Conditionally convert PrimeNG onClear "null" value to the [] to keep data type consistency
    const normalizedValue =
      this.normalizeToArrayOnClear() && value === null ? [] : (value as unknown[])

    this.value.set(normalizedValue)
    this.onChanged?.(normalizedValue)
    this.onTouched?.()
  }

  protected onMobileOpen(): void {
    if (this.isDisabled()) return

    this._value.set([...this.selectedValues()])
    this.searchQuery.set('')
    this.visible.set(true)
  }

  /**
   * The tap that opens the sheet also opens PrimeNG's own panel, hidden behind it. Left open, the panel would be the
   * topmost layer, which takes Escape and the Android back button away from the sheet.
   */
  protected closePrimePanel(): void {
    this.pMultiSelect()?.hide()
  }

  protected applyValue(): void {
    this.visible.set(false)
    // An unchanged selection keeps the form pristine and a null value null, as app-select does
    if (this.isDisabled() || this.isSameSelection(this._value(), this.selectedValues())) return
    this.updateValue(this._value())
  }

  protected cancelValue(): void {
    this.visible.set(false)
  }

  protected onVisibleChange(visible: boolean): void {
    if (!visible) {
      this.cancelValue()
    }
  }

  protected isOptionSelected(option: V): boolean {
    return this.includesValue(this._value(), this.getOptionValue(option))
  }

  protected toggleAll(checked: boolean): void {
    const values = this.toggleableValues()
    const current = this._value()

    this._value.set(
      checked
        ? [...current, ...values.filter((value) => !this.includesValue(current, value))]
        : current.filter((value) => !this.includesValue(values, value)),
    )
  }

  protected toggleOption(option: V): void {
    if (this.isOptionDisabled(option)) return

    const optionValue = this.getOptionValue(option)
    const current = this._value()

    this._value.set(
      this.includesValue(current, optionValue)
        ? current.filter((value) => !this.valuesEqual(value, optionValue))
        : [...current, optionValue],
    )
  }

  // The fallbacks mirror PrimeNG, so SelectItem options need no optionLabel/optionValue
  protected getOptionValue(option: V): unknown {
    const optionValue = this.optionValue()
    if (optionValue) {
      return this.optionField(option, optionValue)
    }
    const value = this.optionField(option, 'value')
    return !this.optionLabel() && value !== undefined ? value : option
  }

  protected getOptionLabel(option: V): string {
    const optionLabel = this.optionLabel()
    const label = optionLabel
      ? this.optionField(option, optionLabel)
      : (this.optionField(option, 'label') ?? option)
    return String(label ?? '')
  }

  protected isOptionDisabled(option: V): boolean {
    return Boolean(this.optionField(option, 'disabled'))
  }

  protected includesValue(values: unknown[], value: unknown): boolean {
    return values.some((item) => this.valuesEqual(item, value))
  }

  protected isSameSelection(a: unknown[], b: unknown[]): boolean {
    return a.length === b.length && a.every((value) => this.includesValue(b, value))
  }

  // Without a dataKey PrimeNG compares values deeply, so the sheet does the same
  protected valuesEqual(a: unknown, b: unknown): boolean {
    const key = this.dataKey()
    return key ? this.resolveByKey(a, key) === this.resolveByKey(b, key) : isEqual(a, b)
  }

  private optionField(option: V, field: string): unknown {
    return option !== null && typeof option === 'object'
      ? this.resolveByKey(option, field)
      : undefined
  }

  private resolveByKey(obj: unknown, key: string): unknown {
    if (obj == null || typeof obj !== 'object') return obj
    return key
      .split('.')
      .reduce<unknown>(
        (acc, part) => (acc == null ? acc : (acc as Record<string, unknown>)[part]),
        obj,
      )
  }
}
